import { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import { CloudUpload, Print, School } from '@mui/icons-material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { generateContent } from '../utils/ai';

function SamplePaper() {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paperContent, setPaperContent] = useState('');
  const paperRef = useRef(null);
  const [paperConfig, setPaperConfig] = useState({
    subject: '',
    duration: 60,
    totalMarks: 100,
    mcqQuestions: 5,
    shortQuestions: 5,
    longQuestions: 3,
    includesMCQ: true
  });

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      if (uploadedFile.type === 'application/pdf' || uploadedFile.type === 'text/plain') {
        setFile(uploadedFile);
        setError('');

        try {
          let content = '';
          if (uploadedFile.type === 'text/plain') {
            content = await uploadedFile.text();
          } else {
            const pdf = await import('pdfjs-dist');
            pdf.GlobalWorkerOptions.workerSrc = new URL(
              'pdfjs-dist/build/pdf.worker.mjs',
              import.meta.url
            ).href;
            const arrayBuffer = await uploadedFile.arrayBuffer();
            const loadingTask = pdf.getDocument({ data: arrayBuffer });
            const pdfDoc = await loadingTask.promise;

            for (let i = 1; i <= pdfDoc.numPages; i++) {
              const page = await pdfDoc.getPage(i);
              const textContent = await page.getTextContent();
              content += textContent.items.map(item => item.str).join(' ');
            }
          }
          setText(content);
        } catch (err) {
          setError('Failed to extract text from file. Please try copying the text manually.');
          console.error('File processing error:', err);
        }
      } else {
        setError('Please upload a PDF or text file');
      }
    }
  };

  const generatePaper = async () => {
    if (!file && !text.trim()) {
      setError('Please upload a file or enter text');
      return;
    }

    if (!paperConfig.subject) {
      setError('Please enter a subject name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const content = text || await file.text();

      // Calculate mark distribution based on which sections are included
      let mcqMarks = 0;
      let shortMarks = 0;
      let longMarks = 0;

      // Calculate section weights
      if (paperConfig.includesMCQ) {
        mcqMarks = Math.round(paperConfig.totalMarks * 0.3); // 30% for MCQs if included
        shortMarks = Math.round(paperConfig.totalMarks * 0.3); // 30% for short questions
        longMarks = paperConfig.totalMarks - mcqMarks - shortMarks; // Remaining marks for long questions
      } else {
        // Original distribution if no MCQs
        shortMarks = Math.round(paperConfig.totalMarks * 0.4);
        longMarks = paperConfig.totalMarks - shortMarks;
      }

      // Calculate marks per question
      const marksPerMCQ = mcqMarks / paperConfig.mcqQuestions;
      const marksPerShort = shortMarks / paperConfig.shortQuestions;
      const marksPerLong = longMarks / paperConfig.longQuestions;

      const messages = [
          {
            role: 'system',
            content: `You are an expert at creating academic exam papers. Generate a complete exam paper following this format:

[SUBJECT NAME]
Time: ${paperConfig.duration} minutes
Total Marks: ${paperConfig.totalMarks}

Instructions:
- Read all questions carefully
- Write your answers clearly and show your work
- Manage your time wisely
${paperConfig.includesMCQ ? `
Section A: Multiple Choice Questions (${mcqMarks} marks)
[Generate ${paperConfig.mcqQuestions} multiple-choice questions worth ${marksPerMCQ} marks each]
- Each question should have 4 options (A, B, C, D) with exactly one correct answer
- Questions should be clear and unambiguous
` : ''}

${paperConfig.includesMCQ ? 'Section B' : 'Section A'}: Short Questions (${shortMarks} marks)
[Generate ${paperConfig.shortQuestions} short questions worth ${marksPerShort} marks each]

${paperConfig.includesMCQ ? 'Section C' : 'Section B'}: Long Questions (${longMarks} marks)
[Generate ${paperConfig.longQuestions} detailed questions worth ${marksPerLong} marks each]

For each question:
1. Make it clear and unambiguous
2. Include mark allocation
3. Use appropriate difficulty level
4. Test understanding and application
5. Include any necessary diagrams or data (described in text)
${paperConfig.includesMCQ ? `
For multiple-choice questions:
1. Each question should have exactly one correct answer
2. Make all options plausible
3. Avoid "all of the above" or "none of the above" options
4. Keep options similar in length and style
5. IMPORTANT: Do NOT indicate the correct answer anywhere in the generated paper
` : ''}`
          },
          {
            role: 'user',
            content: `Generate an exam paper for ${paperConfig.subject} based on this content:\n\n${content}`
          }
      ];

      const paperText = await generateContent(messages, { max_tokens: 2000, temperature: 0.7 });
      setPaperContent(paperText);
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to generate paper';
      setError(errorMessage);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const printPaper = () => {
    const content = paperRef.current;
    const originalContents = document.body.innerHTML;

    // Get the processed content with better MCQ formatting
    const processedContent = processMarkdown(paperContent);

    // Create a temporary element to hold the content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content.innerHTML;

    document.body.innerHTML = `
      <style>
        @media print {
          body {
            font-family: 'Times New Roman', Times, serif;
            line-height: 1.6;
            padding: 20mm;
          }
          h1, h2, h3, h4 {
            margin-top: 20px;
            margin-bottom: 10px;
          }
          strong {
            font-weight: bold;
          }
          ul, li {
            margin-left: 0;
            padding-left: 20px;
          }
          .mcq-option {
            margin-bottom: 5px;
          }
          .mcq-correct {
            font-weight: bold;
          }
          pre {
            white-space: pre-wrap;
            font-family: 'Times New Roman', Times, serif;
            margin: 0;
          }
          .section-header {
            margin-top: 15mm;
            margin-bottom: 5mm;
            font-weight: bold;
            font-size: 14pt;
          }
          .answer-key-section {
            page-break-before: always;
          }
        }
      </style>
      <div>
        ${tempDiv.innerHTML}
      </div>
    `;

    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const processMarkdown = (content) => {
    let processed = content;

    // Section headers and titles
    processed = processed.replace(/\[([^\]]+)\]/g, '# $1');
    processed = processed.replace(/Section [A-C]:[^\n]+/g, '## $1');

    // Instructions and key points
    processed = processed.replace(/Instructions:/g, '### Instructions:');
    processed = processed.replace(/^- (.+)$/gm, '* $1');

    // Question formatting
    processed = processed.replace(/(\d+\.) (.*?)(\n\s*)(A\)) (.*?)(\n\s*)(B\)) (.*?)(\n\s*)(C\)) (.*?)(\n\s*)(D\)) (.*?)(?=\n)/g,
      '### $1 **$2**\n\n- A) $5\n- B) $8\n- C) $11\n- D) $14\n'
    );

    // Format short/long questions
    processed = processed.replace(/(\d+\.) ([^\n]+)/g, '### $1 **$2**');

    // Emphasis on marks and time
    processed = processed.replace(/(\d+)\s*marks?/gi, '**$1 marks**');
    processed = processed.replace(/Time:\s*(\d+)\s*minutes/i, '**Time:** $1 minutes');

    return processed;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Generate Sample Paper
      </Typography>

      {!paperContent ? (
        <Stack spacing={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Paper Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={paperConfig.subject}
                  onChange={(e) => setPaperConfig(prev => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Duration (minutes)"
                  value={paperConfig.duration}
                  onChange={(e) => setPaperConfig(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                  inputProps={{ min: 30, max: 180 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Total Marks"
                  value={paperConfig.totalMarks}
                  onChange={(e) => setPaperConfig(prev => ({ ...prev, totalMarks: parseInt(e.target.value) || 100 }))}
                  inputProps={{ min: 50, max: 200 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>Include Multiple Choice Questions</Typography>
                  <Button
                    variant={paperConfig.includesMCQ ? "contained" : "outlined"}
                    color={paperConfig.includesMCQ ? "primary" : "inherit"}
                    onClick={() => setPaperConfig(prev => ({ ...prev, includesMCQ: true }))}
                    sx={{ mr: 1 }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant={!paperConfig.includesMCQ ? "contained" : "outlined"}
                    color={!paperConfig.includesMCQ ? "primary" : "inherit"}
                    onClick={() => setPaperConfig(prev => ({ ...prev, includesMCQ: false }))}
                  >
                    No
                  </Button>
                </Box>
              </Grid>
              {paperConfig.includesMCQ && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Multiple Choice Questions"
                    value={paperConfig.mcqQuestions}
                    onChange={(e) => setPaperConfig(prev => ({ ...prev, mcqQuestions: parseInt(e.target.value) || 5 }))}
                    inputProps={{ min: 4, max: 15 }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Short Questions"
                  value={paperConfig.shortQuestions}
                  onChange={(e) => setPaperConfig(prev => ({ ...prev, shortQuestions: parseInt(e.target.value) || 5 }))}
                  inputProps={{ min: 3, max: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Long Questions"
                  value={paperConfig.longQuestions}
                  onChange={(e) => setPaperConfig(prev => ({ ...prev, longQuestions: parseInt(e.target.value) || 3 }))}
                  inputProps={{ min: 2, max: 5 }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Study Material
            </Typography>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ width: 'fit-content' }}
              >
                Upload File
                <input
                  type="file"
                  onChange={handleFileUpload}
                  hidden
                  accept=".pdf,.txt"
                />
              </Button>
              {file && (
                <Typography variant="body2" color="text.secondary">
                  Selected file: {file.name}
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Or Enter Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter your study material here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
            />
          </Paper>

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={generatePaper}
              disabled={loading}
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <Print />}
            >
              {loading ? 'Generating Paper...' : 'Generate Sample Paper'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box>
          <Paper
            sx={{ p: 4, mb: 3 }}
            ref={paperRef}
          >
            <Typography
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'serif',
                fontSize: '1rem',
                lineHeight: 1.8
              }}
            >
              <ReactMarkdown>{processMarkdown(paperContent)}</ReactMarkdown>
            </Typography>
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setPaperContent('')}
            >
              Generate New Paper
            </Button>
            <Button
              variant="contained"
              onClick={printPaper}
              startIcon={<Print />}
            >
              Print Paper
            </Button>
          </Stack>
        </Box>
      )}
    </Container>
  );
}

export default SamplePaper;
